import { useState } from 'react';
import { Form, Input, Button, message } from 'antd';
import { changePassword } from '../auth/passwordRecovery';
import { useAuthUser } from '../auth/session';

export interface ChangePasswordFormLabels {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  enterCurrent: string;
  enterNew: string;
  confirmNew: string;
  mismatch: string;
  tooShort: string;
  updated: string;
  submit: string;
}

const DEFAULT_LABELS: ChangePasswordFormLabels = {
  currentPassword: 'Current password',
  newPassword: 'New password',
  confirmPassword: 'Confirm new password',
  enterCurrent: 'Enter current password',
  enterNew: 'Enter new password',
  confirmNew: 'Confirm new password',
  mismatch: 'Passwords do not match',
  tooShort: 'Password must be at least 8 characters',
  updated: 'Password updated',
  submit: 'Update password',
};

export interface ChangePasswordFormProps {
  /** Optional primary button color */
  primaryColor?: string;
  onSuccess?: () => void;
  /** i18n strings from the host portal (English defaults if omitted) */
  labels?: Partial<ChangePasswordFormLabels>;
}

export function ChangePasswordForm({
  primaryColor,
  onSuccess,
  labels: labelsProp,
}: ChangePasswordFormProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const user = useAuthUser();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const email = user?.email;
  if (!email) {
    return null;
  }

  const handleFinish = async (values: {
    current: string;
    password: string;
    confirm: string;
  }) => {
    if (values.password !== values.confirm) {
      message.error(labels.mismatch);
      return;
    }
    if (values.password.length < 8) {
      message.error(labels.tooShort);
      return;
    }
    setLoading(true);
    try {
      const { error } = await changePassword(email, values.current, values.password);
      if (error) {
        message.error(error.message);
        return;
      }
      message.success(labels.updated);
      form.resetFields();
      onSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={(v) => void handleFinish(v)} style={{ maxWidth: 400 }}>
      <Form.Item
        name="current"
        label={labels.currentPassword}
        rules={[{ required: true, message: labels.enterCurrent }]}
      >
        <Input.Password autoComplete="current-password" />
      </Form.Item>
      <Form.Item
        name="password"
        label={labels.newPassword}
        rules={[{ required: true, message: labels.enterNew }]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item
        name="confirm"
        label={labels.confirmPassword}
        dependencies={['password']}
        rules={[{ required: true, message: labels.confirmNew }]}
      >
        <Input.Password autoComplete="new-password" />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          style={primaryColor ? { background: primaryColor, borderColor: primaryColor } : undefined}
        >
          {labels.submit}
        </Button>
      </Form.Item>
    </Form>
  );
}

export default ChangePasswordForm;
